import { useCallback } from 'react';
import {
	type FieldValues,
	type Path,
	type PathValue,
	useController,
	useFormContext,
	type UseFormResetField,
	type UseFormSetValue
} from 'react-hook-form';

export function useFormElement<T extends FieldValues, FP extends Path<T> = Path<T>>({
	defaultValue,
	name
}: {
	defaultValue?: PathValue<T, FP>;
	name: FP;
}) {
	const { control, resetField, setValue: _setValue } = useFormContext<T>();

	const { field, fieldState, formState } = useController<T, FP>({
		control,
		defaultValue,
		name
	});

	const clear = useCallback(
		(options?: Parameters<UseFormResetField<T>>[1]) => resetField(name, options),
		[name, resetField]
	);

	const setValue = useCallback(
		(value: Parameters<UseFormSetValue<T>>[1]) => _setValue(name, value),
		[name, _setValue]
	);

	return {
		clear,
		field,
		fieldState,
		formState,
		setValue
	};
}
